import React, { useState, useEffect } from 'react';
import { Search, ChevronLeft, ChevronRight, AlertTriangle, Ban, FileText } from 'lucide-react';
import { fetchFuelPriceReports, invalidateFuelPriceReport } from '../services/api.ts';
import { FuelPriceReport } from '../types/fuel.ts';
import './FuelPriceReports.css';

const FuelPriceReports: React.FC = () => {
  const [reports, setReports] = useState<FuelPriceReport[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterValid, setFilterValid] = useState<string>('all');
  const [searchStation, setSearchStation] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const perPage = 20;

  useEffect(() => {
    loadReports();
  }, [currentPage, filterValid]);

  const loadReports = async () => {
    try {
      setLoading(true);
      const offset = (currentPage - 1) * perPage;
      const isValid = filterValid === 'all' ? undefined : filterValid === 'valid';
      const data = await fetchFuelPriceReports(offset, perPage, undefined, undefined, undefined, isValid);
      setReports(data.items || []);
      setTotal(data.total || 0);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleInvalidate = async (reportId: number) => {
    if (!window.confirm('Invalidate this report? This will mark it as invalid.')) return;
    try {
      setActionLoading(reportId);
      await invalidateFuelPriceReport(reportId);
      setReports(prev => prev.map(r => r.id === reportId ? { ...r, is_valid: false } : r));
    } catch (err) {
      console.error('Error invalidating report:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const totalPages = Math.ceil(total / perPage);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const formatPrice = (cents: number) => `$${(cents / 100).toFixed(1)}`;

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const filteredReports = searchStation
    ? reports.filter(r =>
        (r.station_name || '').toLowerCase().includes(searchStation.toLowerCase()) ||
        (r.station_brand || '').toLowerCase().includes(searchStation.toLowerCase()) ||
        (r.user_name || '').toLowerCase().includes(searchStation.toLowerCase())
      )
    : reports;

  if (loading && reports.length === 0) {
    return (
      <div className="fuel-reports-page">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading reports...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fuel-reports-page">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="fuel-reports-page">
      <div className="page-header">
        <h2><FileText size={24} /> Price Reports</h2>
        <div className="page-actions">
          <div className="search-box">
            <Search size={18} />
            <input
              type="text"
              placeholder="Filter by station or user..."
              value={searchStation}
              onChange={(e) => setSearchStation(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="filter-section">
        <div className="filter-group">
          <label>Status:</label>
          <select value={filterValid} onChange={(e) => { setFilterValid(e.target.value); setCurrentPage(1); }}>
            <option value="all">All Reports</option>
            <option value="valid">Valid Only</option>
            <option value="invalid">Invalid Only</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Showing:</label>
          <span style={{ color: '#6366f1', fontWeight: 600 }}>
            {filteredReports.length} of {total} reports
          </span>
        </div>
      </div>

      {filteredReports.length === 0 ? (
        <div className="empty-state"><p>No reports found.</p></div>
      ) : (
        <>
          <div className="fuel-table-container">
            <table className="fuel-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Station</th>
                  <th>Fuel Type</th>
                  <th>Price</th>
                  <th>Reporter</th>
                  <th>Distance</th>
                  <th>Time</th>
                  <th>Valid</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredReports.map(report => (
                  <tr key={report.id} className={!report.is_valid ? 'row-invalid' : ''}>
                    <td className="id-cell">{report.id}</td>
                    <td className="name-cell">
                      <span className="station-name">{report.station_name || '—'}</span>
                      <span className="station-brand">{report.station_brand || ''}</span>
                    </td>
                    <td>
                      <span className="fuel-type-badge">{report.fuel_type_code || '—'}</span>
                    </td>
                    <td className="price-cell">{formatPrice(report.price)}</td>
                    <td className="reporter-cell">{report.user_name}</td>
                    <td className="center-cell">
                      {report.reporter_distance_km !== null ? (
                        <span className={`distance-badge ${report.reporter_distance_km > 5 ? 'far' : 'near'}`}>
                          {report.reporter_distance_km} km
                        </span>
                      ) : '—'}
                    </td>
                    <td>{formatDate(report.reported_at)}</td>
                    <td className="center-cell">
                      <span className={`status-badge ${report.is_valid ? 'valid' : 'invalid'}`}>
                        {report.is_valid ? 'Valid' : 'Invalid'}
                      </span>
                    </td>
                    <td className="actions-cell">
                      {report.is_valid && (
                        <button
                          className="action-btn danger"
                          onClick={() => handleInvalidate(report.id)}
                          disabled={actionLoading === report.id}
                          title="Invalidate report"
                        >
                          <Ban size={15} />
                        </button>
                      )}
                      {report.reporter_distance_km !== null && report.reporter_distance_km > 5 && (
                        <span className="flag-icon" title="Reporter was far from station">
                          <AlertTriangle size={15} />
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button className="pagination-btn" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                <ChevronLeft size={18} /> Previous
              </button>
              <div className="pagination-info">Page {currentPage} of {totalPages}</div>
              <button className="pagination-btn" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
                Next <ChevronRight size={18} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FuelPriceReports;
